import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { securityAvatarApi } from '../api/security-avatar.api';
import { useSaveSecurityPhrase } from '../hooks/useSaveSecurityPhrase';
import { RegistrationApiError } from '../pages/register/api/registration.api';
import {
  PhrasePersonalInfo,
  normalizeSecurityPhrase,
  suggestSecurityPhrase,
  validateSecurityPhrase
} from '../utils/security-phrase';
import { SecurityAvatarPicker } from './SecurityAvatarPicker';

const EXAMPLE_PHRASE = 'blue-tiger-42';
const AVATAR_REQUIRED_ERROR = 'Pick an avatar';
const AVATAR_EXPIRED_ERROR =
  'These avatars have expired. Pick one from the new set.';
const NOTHING_TO_SAVE_ERROR = 'Pick a new avatar or enter a new phrase.';

interface SecuritySetupFormProps {
  /** Shows the avatar picker. */
  showAvatar: boolean;
  /** Shows the phrase field. */
  showPhrase: boolean;
  /** Whether the user already has an avatar; if not, picking one is required. */
  hasAvatar: boolean;
  /** Whether the user already has a phrase; if not, entering one is required. */
  hasPhrase: boolean;
  /** The user's name, phone and email, which the phrase must not repeat. */
  personal?: PhrasePersonalInfo;
  /** Id of the phrase input, unique on the page. */
  phraseInputId: string;
  phraseInputRef?: React.Ref<HTMLInputElement>;
  submitLabel: string;
  /** Rendered between the fields and the submit button. */
  phrasePreview?: (phrase: string) => React.ReactNode;
  /** Runs once everything entered is saved. */
  onSaved: () => void;
}

/**
 * The security avatar picker and the security phrase field, saved together.
 * A field the user already has may be left untouched; at least one change is
 * needed. The avatar is saved first; the phrase then needs a one-time code
 * sent to the user's phone.
 */
export function SecuritySetupForm({
  showAvatar,
  showPhrase,
  hasAvatar,
  hasPhrase,
  personal,
  phraseInputId,
  phraseInputRef,
  submitLabel,
  phrasePreview,
  onSaved
}: Readonly<SecuritySetupFormProps>) {
  const [avatarToken, setAvatarToken] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarRefreshKey, setAvatarRefreshKey] = useState(0);
  const [phrase, setPhrase] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  // Saved here while the phrase still waits for its code.
  const [avatarSaved, setAvatarSaved] = useState(false);
  const avatarRequired = showAvatar && !hasAvatar && !avatarSaved;

  const { requestSave, isSaving, otpModal } = useSaveSecurityPhrase({
    onSaved
  });

  const phraseRequired = showPhrase && !hasPhrase;
  // Checked here as well as by the backend, so the OTP is never sent for a phrase it will refuse.
  const phraseError =
    submitted && showPhrase
      ? validateSecurityPhrase(phrase, { required: phraseRequired, personal })
      : null;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setFormError(null);

    const newPhrase = showPhrase ? normalizeSecurityPhrase(phrase) : '';
    if (
      showPhrase &&
      validateSecurityPhrase(phrase, { required: phraseRequired, personal })
    )
      return;
    if (avatarRequired && avatarToken === null) {
      setAvatarError(AVATAR_REQUIRED_ERROR);
      return;
    }
    if (avatarToken === null && newPhrase === '') {
      setFormError(NOTHING_TO_SAVE_ERROR);
      return;
    }

    if (avatarToken !== null) {
      setSavingAvatar(true);
      try {
        await securityAvatarApi.save(avatarToken);
      } catch (error) {
        if (
          error instanceof RegistrationApiError &&
          error.code === 'SECURITY_AVATAR_INVALID'
        ) {
          setAvatarToken(null);
          setAvatarError(AVATAR_EXPIRED_ERROR);
          setAvatarRefreshKey((key) => key + 1);
        } else {
          toast.error(
            (error instanceof Error && error.message) ||
              'Failed to save security avatar. Please try again.'
          );
        }
        return;
      } finally {
        setSavingAvatar(false);
      }
      setAvatarToken(null);
      setAvatarSaved(true);
      if (newPhrase === '') {
        toast.success('Security avatar saved');
        onSaved();
        return;
      }
    }
    requestSave(newPhrase);
  };

  return (
    <>
      <form className="flex flex-col" onSubmit={onSubmit} noValidate>
        {showAvatar && (
          <div>
            <SecurityAvatarPicker
              loadCandidates={securityAvatarApi.getCandidates}
              required={avatarRequired}
              value={avatarToken}
              onChange={(token) => {
                setAvatarToken(token);
                setAvatarError(null);
                setFormError(null);
              }}
              refreshKey={avatarRefreshKey}
            />
            {avatarError && (
              <p
                id={`${phraseInputId}-avatar-error`}
                className="mt-1 text-xs text-red-500"
                role="alert"
              >
                {avatarError}
              </p>
            )}
          </div>
        )}

        {showPhrase && (
          <>
            <div
              className={`flex items-center justify-between ${
                showAvatar ? 'mt-5' : ''
              }`}
            >
              <label
                htmlFor={phraseInputId}
                className="text-sm font-medium text-gray-700"
              >
                {hasPhrase ? 'New security phrase' : 'Your security phrase'}
                {phraseRequired && <span className="text-red-500"> *</span>}
              </label>
              <button
                type="button"
                onClick={() => {
                  setPhrase(suggestSecurityPhrase());
                  setFormError(null);
                }}
                className="text-sm font-semibold text-[#2E6B4F] underline underline-offset-2 hover:text-[#245640]"
              >
                Suggest one
              </button>
            </div>
            <input
              id={phraseInputId}
              ref={phraseInputRef}
              type="text"
              value={phrase}
              onChange={(e) => {
                setPhrase(e.target.value);
                setFormError(null);
              }}
              placeholder={
                hasPhrase ? 'Leave blank to keep it' : `e.g. ${EXAMPLE_PHRASE}`
              }
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={!!phraseError}
              aria-describedby={
                phraseError
                  ? `${phraseInputId}-tip ${phraseInputId}-error`
                  : `${phraseInputId}-tip`
              }
              className={`mt-2 block w-full rounded-lg border ${
                phraseError ? 'border-red-500' : 'border-gray-400'
              } px-3 py-2.5 text-sm focus:border-[#2E6B4F] focus:outline-none focus:ring-1 focus:ring-[#2E6B4F]`}
            />
            {phraseError && (
              <p
                id={`${phraseInputId}-error`}
                className="mt-1 text-xs text-red-500"
              >
                {phraseError}
              </p>
            )}
            <p
              id={`${phraseInputId}-tip`}
              className="mt-1 text-xs text-gray-600"
            >
              Tip: don&apos;t use your password.
            </p>
            {phrasePreview?.(normalizeSecurityPhrase(phrase) || EXAMPLE_PHRASE)}
          </>
        )}

        {formError && (
          <p className="mt-3 text-center text-xs text-red-500" role="alert">
            {formError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSaving || savingAvatar}
          className="mt-6 w-full rounded-full bg-[#2E6B4F] py-3 text-base font-semibold text-white transition-colors hover:bg-[#245640] disabled:opacity-60"
        >
          {submitLabel}
        </button>
      </form>
      {otpModal}
    </>
  );
}
